import { css } from '@emotion/react';
import { useHover } from '@react-aria/interactions';
import React, { ReactNode } from 'react';

import { fontStyles } from '~src/theme/fontStyles';
import { isMobile } from '~src/utils/browser';

import { colors } from '../theme/colors';
import { mediaQueries } from '../theme/mediaQueries';
import { LogoInlineWrapper } from './logo-inline-wrapper';
import { RadioInputMock } from './radio-input';

export interface ShippingCardHeaderExtensions {
  logo?: ReactNode;
  price?: ReactNode;
  subtitle?: ReactNode;
}

interface ShippingCardUIProps extends React.HTMLAttributes<HTMLElement> {
  disableMobileIndent?: boolean;
  innerRef?: React.RefObject<HTMLDivElement>;
  selected: boolean;
  title: string;
  radioTitle: string;
  /*
   * Some categories may have logo disabled, but we need to reserve a space so
   * that everything is in line.
   */
  reserveLogoSpace?: boolean;
  headerExtensions: ShippingCardHeaderExtensions;
  spacing: 'normal' | 'dense';
  enablePadding?: boolean;
  textStyle?: 'bold' | 'normal';
  ariaLevel?: number;
}

export const ShippingCardUI: React.FC<ShippingCardUIProps> = ({
  children,
  disableMobileIndent,
  innerRef,
  selected,
  title,
  radioTitle,
  reserveLogoSpace,
  headerExtensions: { logo, price, subtitle },
  spacing,
  enablePadding,
  textStyle = 'bold',
  ariaLevel = 3,
  ...props
}) => {
  const { hoverProps, isHovered } = useHover({});

  /**
   * The VoiceOver on iOS works a tad differently in comparison to the MacOS
   */
  const ariaProps = isMobile()
    ? { 'aria-label': `${radioTitle} ${title}`, 'aria-level': ariaLevel, role: 'heading' }
    : { 'aria-hidden': true };

  return (
    <div
      aria-label={title}
      data-test="shipping-choice"
      className="focus-visible-outline"
      css={styles.wrapper(selected, enablePadding)}
      ref={innerRef}
      {...hoverProps}
      {...props}
    >
      <div css={styles.header}>
        <RadioInputMock
          title={radioTitle}
          checked={selected}
          showDarkerBorder={!selected && isHovered}
        />
        <div css={styles.title(textStyle)}>
          <div css={styles.titleContent} data-test="shipping-choice-title" {...ariaProps}>
            {title}
          </div>
          <div css={styles.subtitleContent}>{subtitle}</div>
        </div>
        <div css={styles.logoPriceWrapper}>
          <div css={styles.priceContent}>{price}</div>
          {reserveLogoSpace && <LogoInlineWrapper>{logo}</LogoInlineWrapper>}
        </div>
      </div>
      <div css={styles.body(spacing, disableMobileIndent)}>{children}</div>
    </div>
  );
};

const styles = {
  wrapper: (selected: boolean, enablePadding?: boolean) =>
    css({
      padding: `18px ${enablePadding ? 16 : 0}px 12px`,
      borderTop: `1px solid ${colors.neutral90}`,
      cursor: selected ? 'initial' : 'pointer',
    }),
  header: css({
    display: 'grid',
    gridTemplateColumns: `20px 1fr auto`,
    columnGap: '10px',
    position: 'relative',
    color: colors.black,
    ...fontStyles.px16_20,
    [mediaQueries.S]: {
      ...fontStyles.px14_20,
    },
  }),
  body: (spacing: string, disableMobileIndent?: boolean) =>
    css({
      display: 'grid',
      rowGap: spacing === 'dense' ? '6px' : '16px',
      paddingLeft: '30px',
      color: colors.neutral35,
      ...fontStyles.px14_20,
      [mediaQueries.M]: {
        paddingLeft: disableMobileIndent ? '30px' : '0',
      },
      '& > *:first-child': {
        /*
         * Hack: first item should not overlay the logo.
         */
        marginRight: '42px',
      },
    }),
  title: (textStyle: ShippingCardUIProps['textStyle']) =>
    css({
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      fontVariationSettings: `"wght" ${textStyle === 'normal' ? 400 : 500}`,
    }),
  titleContent: css({
    marginBottom: '2px', // ui hack: header should have an additional 2px margin.
    cursor: 'inherit',
  }),
  subtitleContent: css({
    color: colors.neutral35,
    ...fontStyles.px14_20,
    cursor: 'inherit',
  }),
  priceContent: css({
    fontVariationSettings: '"wght" 500',
  }),
  logoPriceWrapper: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: '16px',
    [mediaQueries.S]: {
      gap: '8px',
      alignItems: 'center',
      flexDirection: 'column-reverse',
    },
  }),
};
